import { Link } from 'react-router-dom';
import { ShopOnboardingID, ShopOnboardingSteps } from '@prisma/client';
import {
  Box,
  ButtonBase,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import CheckRounded from '@mui/icons-material/CheckRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import useGetOnboarding from '../../../hooks/businesses/useGetOnboarding';
import useSkipOnboardingStep from '../../../hooks/businesses/useSkipOnboardingStep';
import {
  ONBOARDING_STEPS_LINK_LABELS,
  ONBOARDING_STEPS_ROUTES,
} from '../../../constants/businesses';

interface Props {
  step: ShopOnboardingSteps;
  isComplete: boolean;
}

function SetupStepRow({ step, isComplete }: Props) {
  const theme = useTheme();
  const { onboardings, refetch } = useGetOnboarding();
  const {
    mutate: skipStep,
    isPending: isSkipping,
    variables: stepBeingSkipped,
  } = useSkipOnboardingStep({
    onSuccess: () => {
      refetch();
    },
  });

  const onboarding = onboardings?.find(onb => onb.stepId === step.id);
  const canSkip = step.isSkippable && onboarding?.status === 'INCOMPLETE';
  const isStepBeingSkipped = stepBeingSkipped === step.id && isSkipping;

  const mark = (
    <Box
      width="22px"
      height="22px"
      borderRadius="50%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      border={`1.5px solid ${
        isComplete
          ? theme.palette.secondary.main
          : theme.palette.lightOutlineGrey.main
      }`}
      bgcolor={isComplete ? theme.palette.secondary.main : 'transparent'}>
      {isStepBeingSkipped ? (
        <CircularProgress size={12} sx={{ color: 'text.disabled' }} />
      ) : (
        <CheckRounded
          className="step-mark-check"
          sx={{
            fontSize: 14,
            color: isComplete ? 'white' : 'text.disabled',
            opacity: isComplete ? 1 : 0,
            transition: 'opacity 120ms ease',
          }}
        />
      )}
    </Box>
  );

  return (
    <Box
      display="flex"
      alignItems="center"
      borderBottom={`1px solid ${theme.palette.lineGrey.main}`}
      sx={{
        ':last-of-type': { borderBottom: 'none' },
        ':hover': { backgroundColor: theme.palette.lightGrey.main },
      }}>
      <Box pl={2.5} py={2.25} display="flex" flexShrink={0}>
        {canSkip ? (
          <ButtonBase
            onClick={() => skipStep(step.id as ShopOnboardingID)}
            disabled={isSkipping}
            disableRipple
            title={`Skip ${step.name}`}
            aria-label={`Skip ${step.name}`}
            sx={{
              borderRadius: '50%',
              // The empty circle is the skip control, so hovering previews it.
              ':hover .step-mark-check': { opacity: 1 },
            }}>
            {mark}
          </ButtonBase>
        ) : (
          mark
        )}
      </Box>

      <Box
        component={Link}
        to={ONBOARDING_STEPS_ROUTES[step.id]}
        flex={1}
        minWidth={0}
        display="flex"
        alignItems="center"
        gap={2}
        pl={2}
        pr={2.5}
        py={2.25}
        sx={{ textDecoration: 'none' }}>
        <Stack minWidth={0} gap={0.25}>
          <Typography
            variant="subtitle2"
            component="p"
            color={isComplete ? 'text.secondary' : 'text.primary'}>
            {step.name}
          </Typography>
          {!!step.description && (
            <Typography variant="caption" color="text.secondary">
              {step.description}
            </Typography>
          )}
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          gap={0.5}
          ml="auto"
          pl={2}
          flexShrink={0}>
          <Typography
            variant="caption"
            fontWeight={600}
            color={theme.palette.primary.main}
            display={{ xs: 'none', md: 'block' }}
            whiteSpace="nowrap">
            {ONBOARDING_STEPS_LINK_LABELS[step.id]}
          </Typography>
          <ChevronRightRounded
            sx={{ fontSize: 16, color: theme.palette.primary.main }}
          />
        </Stack>
      </Box>
    </Box>
  );
}

export default SetupStepRow;
